import { NgxTagCommanderModule, TagCommanderService } from 'ngx-tag-commander';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';

import {
  ChangeDetectionStrategy, Component, HostBinding, inject, OnDestroy, OnInit, PLATFORM_ID, signal, ViewEncapsulation
} from '@angular/core';

import { OutletModule, OutletRefModule, StorefrontComponentModule } from '@spartacus/storefront';
import { CmsService } from '@spartacus/core';

import { LinkService, ILink, BrandsEnum, SdLoaderComponent } from '@seb/web-design-system/core';

import {
  BreadcrumbsComponent, AddedToCartModalComponent, CompatibilityCartModalComponent, PopinNewsletterComponent,
  LiveChatComponent, ToasterComponent, PopinAbandonedCartComponent, IadvizeScriptComponent
} from './components/static/core';

import { PromoBannersComponent } from './templates/promo-banners/promo-banners.component';
import { FooterComponent } from './templates/footer/footer.component';
import { HeaderComponent } from './templates/header/header.component';

import { BaseStoreService, HobStateService, LoaderService, TrackingService } from './services';
import { ScrollService } from './services/scroll.service';
import { PromoBannerOffsetDirective } from './directives';
import { WebCommonModule } from './web-common.module';

const CART_ROUTE = '/cart';

const NO_BREADCRUMB_ROUTES = [
  CART_ROUTE,
  '/checkout/multi/choose-delivery',
  '/checkout/multi/choose-payment-method',
  '/donations',
  '/checkout/express/review',
  '/order-confirmation',
  '/received-order-confirmation',
  '/checkout/express/review',
  '/recipe/detail'
];

@Component({
  selector: 'wc-web-common',
  templateUrl: './web-common.component.html',
  styleUrls: ['./web-common.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    RouterOutlet,
    OutletModule,
    OutletRefModule,
    StorefrontComponentModule,
    PromoBannersComponent,
    HeaderComponent,
    BreadcrumbsComponent,
    SdLoaderComponent,
    AddedToCartModalComponent,
    CompatibilityCartModalComponent,
    PopinNewsletterComponent,
    LiveChatComponent,
    FooterComponent,
    ToasterComponent,
    NgxTagCommanderModule,
    PopinAbandonedCartComponent,
    IadvizeScriptComponent,
    PromoBannerOffsetDirective
  ]
})
export class WebCommonComponent implements OnInit, OnDestroy {
  @HostBinding('id')
  id = 'svgRegistry';

  private readonly tagCommanderService = inject(TagCommanderService);
  private readonly baseStoreService = inject(BaseStoreService);
  private readonly hobStateService = inject(HobStateService);
  private readonly loaderService = inject(LoaderService);
  private readonly scrollService = inject(ScrollService);
  private readonly linkService = inject(LinkService);
  private readonly cmsService = inject(CmsService);
  private readonly trackingService = inject(TrackingService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  public readonly isGlobalLoaderDisplayed = signal(false);
  public readonly disableAddToCartPopup = signal(false);
  public readonly disableBreadcrumbs = signal(false);

  public BrandEnum = BrandsEnum;
  public isPlatformBrowser = false;
  public isLiveChatEnabled = false;
  public brand = BrandsEnum.default;

  private readonly socialList = ['facebook', 'twitter', 'instagram'];
  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const { enableChat } = this.baseStoreService.baseStore;

      this.isLiveChatEnabled = enableChat;
      this.isPlatformBrowser = true;

      this.subscriptions.add(
        this.loaderService.isGlobalLoaderDisplayed$.subscribe((isDisplayed) => {
          this.isGlobalLoaderDisplayed.set(isDisplayed);
        })
      );

      this.watchAppExit();

      this.scrollService.watchScroll();

      if (WebCommonModule.appBrand === BrandsEnum.krups) {
        this.updateGoogleMeta(this.document);
      }

      this.prepareIAdvizeListenersForTracking();
    }

    this.subscriptions.add(
      this.hobStateService.isHob$.subscribe((isHob) => {
        this.brand = isHob ? BrandsEnum.hob : BrandsEnum.default;
      })
    );

    this.watchPageChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Intercept links clicks and trigger TagCo event when the user leave our application
   */
  private watchAppExit(): void {
    this.subscriptions.add(
      this.linkService.onClickLink().subscribe((link: ILink) => {
        const hostname = window.location.origin;
        if (link?.url?.includes('http') || link?.url?.includes('https')) {
          if (!link.url.includes(hostname)) {
            const isSocialLink = this.socialList.some((value) => link.url.includes(value));
            this.tagCommanderService.triggerEvent('exitLink', undefined, {
              linkType: isSocialLink ? 'Social' : 'others',
              linkURL: link.url
            });
          }
        }
      })
    );
  }

  /**
   * Watch acactive page changes to show or hide breadcrumbs and cart popup
   */
  private watchPageChanges(): void {
    this.subscriptions.add(
      this.cmsService.getCurrentPage().pipe(
        filter((p) => !!p)
      ).subscribe((page) => {
        this.disableBreadcrumbs.set(NO_BREADCRUMB_ROUTES.includes(page.label));
        this.disableAddToCartPopup.set(page.label === CART_ROUTE);
      })
    );
  }

  /**
   * Change Google meta keys for Krups site
   * @param document The active document
   * TODO: remove after (because its KRUPS only currently) ?
   */
  private updateGoogleMeta(document: Document): void {
    const market = this.baseStoreService.baseStore.market?.toUpperCase();
    const meta = 'meta[name="google-site-verification"]';

    const googleKeys = {
      DE: 'XmR6ST666pV2nW7AIUBhYMfGxtB2HdsyVj-7yrz_v8E',
      AT: 'htJUrpDrSwZQ0TE7Rb3VxgW9P9wQR0n2yJknV4fc-jw'
    };

    if (market === 'DE') {
      document?.querySelector(meta)?.setAttribute('content', googleKeys['DE']);
    } else if (market === 'AT') {
      document?.querySelector(meta)?.setAttribute('content', googleKeys['AT']);
    }
  }

  private prepareIAdvizeListenersForTracking(): void {
    const win = this.isPlatformBrowser ? window as any : undefined;
    if (win != null) {
      win.iAdvizeCallbacks = {
        ...win.iAdvizeCallbacks,
        onChatDisplayed: () => {
          this.trackingService.sendEvent('chatClick',
            { chatAction: 'Open' }
          );
        },
        onChatHidden: () => {
          this.trackingService.sendEvent('chatClick',
            { chatAction: 'Close' }
          );
        },
        onMessageSent: () => {
          this.trackingService.sendEvent('chatClick',
            { chatAction: 'Ask a question' }
          );
        }
      };
    }
  }
}
